import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboard } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Pill, SkeletonSet } from '@cwos/ui-admin';
import {
  clampExpectedHours,
  integrationStateLabel,
  integrationTone,
  type DashboardPeriod,
} from '../../dashboardLogic.js';
import {
  CareGapsWidget,
  DocumentationWidget,
  ExceptionsWidget,
  ReconciliationWidget,
  ServiceLevelsWidget,
} from '../../widgets.js';
import { Panel, type QueryLike } from '../shared.js';
import { LoadFailure } from '../../../LoadFailure.js';

/**
 * Monitor — the legacy executive-dashboard widgets, preserved as-is alongside
 * the new analytical views. These use the original 7d/30d period control; the
 * shared analytics date controls drive the other views.
 */
export function MonitorView() {
  /**
   * AN ORGANIZATION BENCHMARK, AND IT STARTS EMPTY.
   *
   * This box used to be labelled "Expected weekly hours" and default to 40, and
   * that 40 was the DENOMINATOR the server divided every worker by — so a
   * part-time worker delivering her full contract was published as "below plan"
   * because of a number in a text field nobody had typed in. Plan attainment is
   * now each worker's own contracted hours; this box is the org-wide what-if it
   * always read like ("who is below 32?"), and it is empty until a reader asks
   * the question, because a default nobody chose is the substitution the whole
   * repair exists to remove.
   */
  const [benchmarkInput, setBenchmarkInput] = useState('');
  const parsedBenchmark = Number(benchmarkInput);
  const orgBenchmarkHours =
    benchmarkInput.trim() === '' || !Number.isFinite(parsedBenchmark)
      ? null
      : clampExpectedHours(parsedBenchmark);
  const [period, setPeriod] = useState<DashboardPeriod>('7d');

  const health = useQuery({
    queryKey: ['dashboard', 'integration-health'],
    queryFn: () => dashboard.integrationHealth(),
    refetchInterval: 60_000,
  });

  return (
    <div className="an-stack">
      <Panel
        title="Monitoring widgets"
        subtitle={
          <span className="rn-muted">
            legacy 7d/30d widgets — the analytical views use the shared date controls above
          </span>
        }
      >
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-20)',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          {/* Filter group, not a tab strip — see AnalyticsShell for the reasoning. */}
          <div
            className="rn-tabs"
            role="group"
            aria-label="Widget period"
            style={{ marginBottom: 0 }}
          >
            <button
              type="button"
              className="rn-tab"
              aria-pressed={period === '7d'}
              onClick={() => setPeriod('7d')}
            >
              7 days
            </button>
            <button
              type="button"
              className="rn-tab"
              aria-pressed={period === '30d'}
              onClick={() => setPeriod('30d')}
            >
              30 days
            </button>
          </div>
          <label
            style={{ display: 'grid', gap: 'var(--space-6)', fontSize: 12.5, fontWeight: 600 }}
          >
            Organization benchmark (h/week)
            <input
              type="number"
              min={1}
              max={168}
              placeholder="none"
              aria-describedby="monitor-benchmark-help"
              value={benchmarkInput}
              onChange={(e) => setBenchmarkInput(e.target.value)}
              style={{
                width: 96,
                padding: 'var(--space-8) var(--space-10)',
                border: '1px solid var(--color-line2)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-sunken)',
                font: 'inherit',
              }}
            />
            <span id="monitor-benchmark-help" className="rn-muted" style={{ fontWeight: 400 }}>
              A uniform bar for the whole organization. Each worker is still measured against their
              own contracted hours.
            </span>
          </label>
        </div>
        <MonitorHealth health={health} />
      </Panel>

      <div className="db-grid">
        <ServiceLevelsWidget period={period} orgBenchmarkHours={orgBenchmarkHours} />
        <CareGapsWidget period={period} />
        <DocumentationWidget />
        <ReconciliationWidget period={period} />
        <ExceptionsWidget period={period} />
      </div>
    </div>
  );
}

function MonitorHealth({ health }: { health: QueryLike<DashboardNS.IntegrationHealth> }) {
  if (health.isLoading) return <SkeletonSet surface="band" />;
  if (health.isError && !health.data) {
    return (
      <LoadFailure
        error={health.error}
        message="We couldn’t load integration health just now."
        onRetry={() => void health.refetch()}
      />
    );
  }
  const d = health.data;
  if (!d) return null;
  return (
    <div style={{ marginTop: 'var(--space-16)' }} role="status">
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-8)', flexWrap: 'wrap' }}
      >
        <span style={{ fontWeight: 600 }}>Integration health</span>
        <Pill variant={integrationTone(d)} dot>
          {integrationStateLabel(d.state)}
        </Pill>
      </div>
      <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
        {d.message}
      </p>
    </div>
  );
}
